import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiToggleLeft,
  FiToggleRight,
  FiLayers,
  FiSearch,
  FiDownload
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { AdminLayout } from '../../components/admin';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import ServiceTypeFormModal from '../../components/admin/service-types/ServiceTypeFormModal';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { useDialog } from '../../hooks/useDialog';
import toast from 'react-hot-toast';
import { API_URL } from '../../services/api';
import { exportToExcel } from '../../utils/exportToExcel';

// Types
interface ServiceType {
  id: string;
  nameTh: string;
  nameEn?: string;
  description?: string;
  icon?: string;
  usageCount?: number;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ServiceTypesResponse {
  items: ServiceType[];
  total: number;
}

const ServiceTypesPage: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();

  // State
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<ServiceType | null>(null);

  // API call function
  const callApi = useCallback(async <T = any>(options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    endpoint: string;
    data?: any;
  }): Promise<{ success: boolean; data: T; message?: string }> => {
    if (!token) {
      throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
    }

    
    const response = await fetch(`${API_URL}${options.endpoint}`, {
      method: options.method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
    });

    // Handle 204 No Content response (e.g., DELETE operations)
    const result = response.status === 204 ? {} : await response.json();

    if (!response.ok) {
      throw new Error(result.detail || result.message || `HTTP ${response.status}`);
    }

    return {
      success: true,
      data: result, // API ส่งข้อมูลมาโดยตรง ไม่มี .data wrapper
      message: result.message
    };
  }, [token]);

  // Load service types
  const loadServiceTypes = useCallback(async () => {
    try {
      setLoading(true);
      clearError();

      const response = await callApi<ServiceTypesResponse>({
        method: 'GET',
        endpoint: '/api/admin/service-types/'
      });

      if (response.success && response.data) {
        // API ส่ง {items: [...], total: N} มาโดยตรง
        const data = response.data as ServiceTypesResponse;
        setServiceTypes(data.items || []);
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถโหลดข้อมูลประเภทบริการได้');
    } finally {
      setLoading(false);
    }
  }, [callApi, handleError, clearError]);

  // Initial load
  useEffect(() => {
    if (token) {
      loadServiceTypes();
    }
  }, [loadServiceTypes, token]);

  // Handle delete
  const handleDelete = async (serviceType: ServiceType) => {
    await showConfirm(
      {
        title: 'ยืนยันการลบ',
        message: `ต้องการลบประเภทบริการ "${serviceType.nameTh}" หรือไม่?`,
        confirmText: 'ลบ',
        cancelText: 'ยกเลิก',
        variant: 'danger'
      },
      async () => {
        try {
          const response = await callApi({
            method: 'DELETE',
            endpoint: `/api/admin/service-types/${serviceType.id}`
          });

          if (response.success) {
            toast.success('ลบประเภทบริการเรียบร้อยแล้ว');
            loadServiceTypes();
          }
        } catch (error) {
          handleError(error, 'ไม่สามารถลบประเภทบริการได้');
        }
      }
    );
  };

  // Handle toggle active
  const handleToggleActive = async (serviceType: ServiceType) => {
    try {
      const response = await callApi({
        method: 'POST',
        endpoint: `/api/admin/service-types/${serviceType.id}/toggle-active`
      });

      if (response.success) {
        toast.success(serviceType.isActive ? 'ปิดใช้งานเรียบร้อยแล้ว' : 'เปิดใช้งานเรียบร้อยแล้ว');
        loadServiceTypes();
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถเปลี่ยนสถานะได้');
    }
  };

  // Handle edit
  const handleEdit = (serviceType: ServiceType) => {
    setEditingServiceType(serviceType);
    setShowModal(true);
  };

  // Handle add new
  const handleAddNew = () => {
    setEditingServiceType(null);
    setShowModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    setEditingServiceType(null);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    loadServiceTypes();
    handleModalClose();
  };

  // Handle export to Excel
  const handleExportExcel = () => {
    exportToExcel({
      filename: `service-types-${new Date().toISOString().split('T')[0]}`,
      sheetName: 'ประเภทบริการ',
      columns: [
        { header: 'ลำดับ', key: 'displayOrder', width: 10 },
        { header: 'ชื่อภาษาไทย', key: 'nameTh', width: 30 },
        { header: 'ชื่อภาษาอังกฤษ', key: 'nameEn', width: 30 },
        { header: 'คำอธิบาย', key: 'description', width: 40 },
        { header: 'จำนวนบริษัทที่ใช้', key: 'usageCount', width: 15 },
        { header: 'สถานะ', key: 'statusText', width: 12 },
        { header: 'วันที่สร้าง', key: 'createdAt', width: 18 },
      ],
      data: filteredServiceTypes.map(st => ({
        ...st,
        statusText: st.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน',
        createdAt: new Date(st.createdAt).toLocaleDateString('th-TH'),
      }))
    });

    toast.success('ส่งออกข้อมูลเรียบร้อยแล้ว');
  };

  // Filter service types
  const filteredServiceTypes = serviceTypes.filter((st) => {
    // Filter by search term
    const matchesSearch = st.nameTh.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (st.nameEn && st.nameEn.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filter by status
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && st.isActive) ||
      (statusFilter === 'inactive' && !st.isActive);

    return matchesSearch && matchesStatus;
  });

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                <FiLayers className="mr-2 sm:mr-3 h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                จัดการประเภทบริการ
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-600">
                จัดการประเภทบริการของบริษัทรักษาความปลอดภัย (ทั้งหมด {serviceTypes.length} รายการ)
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:space-x-3">
              <Button
                variant="secondary"
                leftIcon={<FiDownload />}
                onClick={handleExportExcel}
                disabled={loading || serviceTypes.length === 0}
                className="text-sm"
              >
                <span className="hidden sm:inline">Export Excel</span>
                <span className="sm:hidden">Export</span>
              </Button>
              <Button
                variant="secondary"
                leftIcon={<FiRefreshCw />}
                onClick={() => loadServiceTypes()}
                disabled={loading}
                className="text-sm"
              >
                รีเฟรช
              </Button>
              <Button
                variant="default"
                leftIcon={<FiPlus />}
                onClick={handleAddNew}
                className="text-sm"
              >
                <span className="hidden sm:inline">เพิ่มประเภทบริการ</span>
                <span className="sm:hidden">เพิ่ม</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorState.message && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="font-medium">เกิดข้อผิดพลาด</p>
            <p className="text-sm">{errorState.message}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Input */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาประเภทบริการ..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <label htmlFor="status-filter" className="form-label mb-0 text-sm font-medium text-gray-700 whitespace-nowrap">
              กรองตามสถานะ:
            </label>
            <select
              id="status-filter"
              className="form-input text-sm min-w-[200px]"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            >
              <option value="all">ทั้งหมด ({serviceTypes.length})</option>
              <option value="active">
                ใช้งานอยู่ ({serviceTypes.filter(st => st.isActive).length})
              </option>
              <option value="inactive">
                ปิดใช้งาน ({serviceTypes.filter(st => !st.isActive).length})
              </option>
            </select>
          </div>
        </div>

        {/* Service Types Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
                <span className="ml-2 text-gray-600">กำลังโหลด...</span>
              </div>
            ) : filteredServiceTypes.length === 0 ? (
              <div className="text-center py-12">
                <FiLayers className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm ? 'ไม่พบประเภทบริการที่ค้นหา' : 'ยังไม่มีประเภทบริการ'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? 'ลองเปลี่ยนคำค้นหา' : 'เริ่มต้นด้วยการเพิ่มประเภทบริการใหม่'}
                </p>
                {!searchTerm && (
                  <Button
                    variant="default"
                    leftIcon={<FiPlus />}
                    onClick={handleAddNew}
                    className="mt-4"
                  >
                    เพิ่มประเภทบริการแรก
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Mobile Card View (< 768px) */}
                <div className="md:hidden space-y-3 p-4">
                  {filteredServiceTypes
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((serviceType, index) => (
                      <motion.div
                        key={serviceType.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded bg-blue-50 flex-shrink-0">
                              {serviceType.icon ? (
                                <span className="text-2xl">{serviceType.icon}</span>
                              ) : (
                                <FiLayers className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {serviceType.nameTh}
                              </div>
                              {serviceType.nameEn && (
                                <div className="text-xs text-gray-500">
                                  {serviceType.nameEn}
                                </div>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            #{serviceType.displayOrder}
                          </span>
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          <div className="text-xs text-gray-600">
                            จำนวน: <span className="font-medium">{serviceType.usageCount || 0} บริษัท</span>
                          </div>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              serviceType.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {serviceType.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                          </span>
                        </div>

                        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => handleToggleActive(serviceType)}
                            className={`flex items-center space-x-1 text-xs px-3 py-1.5 rounded ${
                              serviceType.isActive
                                ? 'text-yellow-600 hover:bg-yellow-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={serviceType.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                          >
                            {serviceType.isActive ? (
                              <FiToggleRight className="h-4 w-4" />
                            ) : (
                              <FiToggleLeft className="h-4 w-4" />
                            )}
                            <span>{serviceType.isActive ? 'ปิด' : 'เปิด'}</span>
                          </button>
                          <button
                            onClick={() => handleEdit(serviceType)}
                            className="flex items-center space-x-1 text-xs text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded"
                            title="แก้ไข"
                          >
                            <FiEdit2 className="h-4 w-4" />
                            <span>แก้ไข</span>
                          </button>
                          <button
                            onClick={() => handleDelete(serviceType)}
                            className="flex items-center space-x-1 text-xs text-red-600 hover:bg-red-50 px-3 py-1.5 rounded"
                            title="ลบ"
                          >
                            <FiTrash2 className="h-4 w-4" />
                            <span>ลบ</span>
                          </button>
                        </div>
                      </motion.div>
                    ))}
                </div>

                {/* Desktop Table View (>= 768px) */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ลำดับ
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ไอคอน
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ชื่อประเภทบริการ
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          จำนวนบริษัทที่ใช้
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          สถานะ
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          การจัดการ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredServiceTypes
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((serviceType, index) => (
                          <motion.tr
                            key={serviceType.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="hover:bg-gray-50"
                          >
                            {/* ลำดับ */}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {serviceType.displayOrder}
                            </td>

                            {/* ไอคอน */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-center">
                                <div className="flex items-center justify-center w-10 h-10 rounded bg-blue-50">
                                  {serviceType.icon ? (
                                    <span className="text-2xl">{serviceType.icon}</span>
                                  ) : (
                                    <FiLayers className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* ชื่อประเภทบริการ */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {serviceType.nameTh}
                                </div>
                                {serviceType.nameEn && (
                                  <div className="text-sm text-gray-500">
                                    {serviceType.nameEn}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* จำนวนบริษัทที่ใช้ */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="text-sm font-medium text-gray-900">
                                {serviceType.usageCount || 0} บริษัท
                              </div>
                            </td>

                            {/* สถานะ */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  serviceType.isActive
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {serviceType.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                              </span>
                            </td>

                            {/* การจัดการ */}
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => handleToggleActive(serviceType)}
                                  className={`p-1 ${
                                    serviceType.isActive
                                      ? 'text-yellow-600 hover:text-yellow-900'
                                      : 'text-green-600 hover:text-green-900'
                                  }`}
                                  title={serviceType.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                                >
                                  {serviceType.isActive ? (
                                    <FiToggleRight className="h-5 w-5" />
                                  ) : (
                                    <FiToggleLeft className="h-5 w-5" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleEdit(serviceType)}
                                  className="text-blue-600 hover:text-blue-900 p-1"
                                  title="แก้ไข"
                                >
                                  <FiEdit2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(serviceType)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="ลบ"
                                >
                                  <FiTrash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Service Type Form Modal */}
        <ServiceTypeFormModal
          isOpen={showModal}
          onClose={handleModalClose}
          serviceType={editingServiceType}
          onSuccess={handleModalSuccess}
        />

        {ConfirmDialog}
        {AlertDialog}
      </div>
    </AdminLayout>
  );
};

export default ServiceTypesPage;
